import { useCallback, useEffect, useState } from "react"
import { useNavigate } from "react-router-dom"
import {
  Alert,
  Avatar,
  AvatarGroup,
  Box,
  Button,
  Chip,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Divider,
  LinearProgress,
  Link,
  Stack,
  Typography,
} from "@mui/material"
import OpenIcon from "@mui/icons-material/OpenInNewOutlined"

import { projectApi, taskApi } from "../../api"
import { LoadingScreen } from "../../components/LoadingState"
import { useTimeSummary } from "../../time/useTimeSummary"
import TimeBreakdown, { TimeFigure } from "../../time/TimeBreakdown"
import { decimalHours } from "../../time/duration"
import { avatarColor, formatDate, initialsOf } from "../../utils/format"
import { PRIORITY_COLORS, STATUS_FALLBACK_COLORS } from "../../theme"
import { filledChipSx } from "../../theme/contrast"
import { rise } from "../../motion"

/**
 * A project, without leaving the page you were reading.
 *
 * ── Why a dialog as well as a page ──────────────────────────────────────────
 *
 * A project name turns up on a timesheet row, a task, a chat thread and an
 * invoice line, and in every one of those places the question is the same and
 * small: what is this, who is on it, how much has gone into it. Answering it by
 * navigating means losing the week, the filter or the thread that raised the
 * question — and then finding it again.
 *
 * So this is the same information as the project page, read-only, over the top
 * of wherever you were. Editing still lives on the page, which is what the
 * button in the corner is for: a modal is the wrong place to change something
 * other people are also looking at.
 *
 * @param {number|string|null} projectId Null closes it, so a caller can hold one
 *   piece of state rather than an id and a boolean that can disagree.
 */
export default function ProjectDetailDialog({ projectId, onClose }) {
  const navigate = useNavigate()

  const [data, setData] = useState(null)
  const [tasks, setTasks] = useState([])
  const [loading, setLoading] = useState(false)
  const [error, setError] = useState(null)

  /* Hours for the project, and the label that says whose they are: without
     `view_all` these are the viewer's own, and calling that "Time logged" on a
     project three people work on would be a lie. */
  const time = useTimeSummary({ projectId: projectId ? Number(projectId) : null })

  /* Per task, with each step's hours counted in its task — the rows below are
     tasks, and a task whose steps carry the time would otherwise read as idle. */
  const perTask = useTimeSummary({ projectId: projectId ? Number(projectId) : null, groupBy: "top_task" })
  const secondsFor = (taskId) =>
    Number(perTask.groups.find((group) => Number(group.group_key) === Number(taskId))?.tracked_seconds) || 0

  /* Which figure's breakdown is open: the project's, or one task's. */
  const [breakdown, setBreakdown] = useState(null)

  const load = useCallback(async () => {
    if (!projectId) return
    setLoading(true)
    setError(null)
    try {
      const [projectData, taskData] = await Promise.all([
        projectApi.get(projectId),
        /*
         * The tasks, not the steps inside them.
         *
         * Asking for everything flattened the two together: a project of three
         * tasks with ten steps between them read as thirteen tasks, "0 of 13
         * done", and a list whose first entry was a task and whose next five
         * were parts of it, side by side and indistinguishable. The steps
         * belong to the row above them, and that is what the count beside each
         * one says.
         */
        taskApi.list({ project_id: projectId, top_level: "true", limit: 100 }),
      ])
      setData(projectData)
      setTasks(taskData.tasks || [])
    } catch (err) {
      setError(err.userMessage)
      setData(null)
    } finally {
      setLoading(false)
    }
  }, [projectId])

  useEffect(() => {
    // Cleared on open, not on close: leaving the previous project on screen
    // while the next one loads shows somebody the wrong figures under the right
    // name, which is worse than a spinner.
    setData(null)
    setTasks([])
    load()
  }, [load])

  const project = data?.project
  const members = data?.members || []
  const done = tasks.filter((t) => t.is_terminal).length
  const progress = tasks.length > 0 ? Math.round((done / tasks.length) * 100) : 0

  return (
    <Dialog open={Boolean(projectId)} onClose={onClose} maxWidth="sm" fullWidth>
      <DialogTitle>
        {project?.name || "Project"}
        <Typography variant="caption" color="text.secondary" display="block">
          {[project?.code, project?.client_id !== "0" ? project?.client_label : null].filter(Boolean).join(" · ") ||
            "Loading…"}
        </Typography>
      </DialogTitle>

      <DialogContent dividers>
        {loading && !project ? <LoadingScreen minHeight={220} /> : null}
        {error ? <Alert severity="error">{error}</Alert> : null}

        {project ? (
          <Stack spacing={2}>
            {project.description ? (
              <Typography variant="body2" color="text.secondary">
                {project.description}
              </Typography>
            ) : null}

            <Stack direction="row" spacing={3} flexWrap="wrap" useFlexGap>
              <Fact label="Status" value={String(project.status || "").replace("_", " ")} capitalize />
              <Fact label="Owner" value={project.owner_name || "Nobody"} />
              <Fact label="Start" value={formatDate(project.start_date) || "—"} />
              <Fact label="Due" value={formatDate(project.due_date) || "—"} />
              {time.mayView ? (
                <Fact
                  label={time.label}
                  value={
                    time.trackedSeconds > 0 ? (
                      <>
                        <TimeFigure
                          seconds={time.trackedSeconds}
                          label="Who logged time on this project"
                          onOpen={(anchor) => setBreakdown({ anchor, title: project.name, query: { project_id: project.id } })}
                        />
                        {` · ${decimalHours(time.trackedSeconds)} h`}
                      </>
                    )
                      : "None yet"
                  }
                />
              ) : null}
            </Stack>

            <Divider />

            <Box>
              <Stack direction="row" alignItems="center" spacing={1} sx={{ mb: 1 }}>
                <Typography variant="body2" fontWeight={650} sx={{ flex: 1 }}>
                  Tasks
                </Typography>
                <Typography variant="caption" color="text.secondary">
                  {done} of {tasks.length} done
                </Typography>
              </Stack>
              {tasks.length > 0 ? (
                <LinearProgress variant="determinate" value={progress} sx={{ height: 5, borderRadius: 3, mb: 1.5 }} />
              ) : null}

              {tasks.length === 0 ? (
                <Typography variant="body2" color="text.secondary">
                  No tasks in this project yet.
                </Typography>
              ) : (
                /* Capped, with the count saying what is behind the cap. A modal
                   that scrolls for two hundred rows has become a page, and
                   badly — the page is one click away and is better at it. */
                <Stack spacing={0.25}>
                  {tasks.slice(0, 6).map((task, index) => (
                    <Stack key={task.id} direction="row" spacing={1} alignItems="center" sx={{ py: 0.4, ...rise(index) }}>
                      <Box
                        sx={{ width: 6, height: 6, borderRadius: "50%", bgcolor: PRIORITY_COLORS[task.priority], flexShrink: 0 }}
                      />
                      <Typography variant="caption" sx={{ fontFamily: "monospace", color: "text.secondary", flexShrink: 0 }}>
                        {task.task_code}
                      </Typography>
                      <Typography
                        variant="body2"
                        noWrap
                        sx={{
                          flex: 1,
                          textDecoration: task.is_terminal ? "line-through" : "none",
                          opacity: task.is_terminal ? 0.6 : 1,
                        }}
                      >
                        {task.title}
                      </Typography>
                      {/* How much of this one is done, where the steps used to
                          be listed as tasks of their own. Same shape as the
                          project page, so the two read alike. */}
                      <TimeFigure
                        seconds={secondsFor(task.id)}
                        label={`Who logged time on ${task.task_code}`}
                        onOpen={(anchor) =>
                          setBreakdown({
                            anchor,
                            title: `${task.task_code} · ${task.title}`,
                            query: { task_id: task.id, with_steps: "true" },
                          })
                        }
                        sx={{ fontSize: 12, color: "text.secondary", flexShrink: 0 }}
                      />
                      {task.subtask_total > 0 ? (
                        <Chip
                          size="small"
                          variant="outlined"
                          label={`${task.subtask_done}/${task.subtask_total}`}
                          title={`${task.subtask_done} of ${task.subtask_total} steps done`}
                          sx={{ height: 18, fontSize: 10, flexShrink: 0 }}
                        />
                      ) : null}
                      <Chip
                        size="small"
                        label={task.status_label}
                        sx={{
                          height: 20,
                          fontSize: 10,
                          flexShrink: 0,
                          ...filledChipSx(task.status_color || STATUS_FALLBACK_COLORS[task.status_name]),
                        }}
                      />
                    </Stack>
                  ))}
                  {tasks.length > 6 ? (
                    <Link
                      component="button"
                      type="button"
                      variant="caption"
                      onClick={() => {
                        onClose()
                        navigate(`/projects/${project.id}`)
                      }}
                      sx={{ alignSelf: "flex-start", mt: 0.5 }}
                    >
                      {tasks.length - 6} more on the project
                    </Link>
                  ) : null}
                </Stack>
              )}
            </Box>

            {members.length > 0 ? (
              <>
                <Divider />
                <Stack direction="row" alignItems="center" spacing={1.5}>
                  <Typography variant="body2" fontWeight={650}>
                    Team
                  </Typography>
                  <AvatarGroup max={8} sx={{ "& .MuiAvatar-root": { width: 28, height: 28, fontSize: 11 } }}>
                    {members.map((member) => (
                      <Avatar key={member.user_id} title={member.name} sx={{ bgcolor: avatarColor(member.name || "?") }}>
                        {initialsOf(member.name || member.email || "?")}
                      </Avatar>
                    ))}
                  </AvatarGroup>
                </Stack>
              </>
            ) : null}
          </Stack>
        ) : null}
      </DialogContent>

      <DialogActions>
        {/* Changing a project belongs on the project, not in a panel somebody
            opened to check a figure. */}
        <Button
          startIcon={<OpenIcon />}
          disabled={!project}
          onClick={() => {
            onClose()
            navigate(`/projects/${project.id}`)
          }}
        >
          Open the project
        </Button>
        <Box sx={{ flex: 1 }} />
        <Button onClick={onClose}>Close</Button>
      </DialogActions>

      <TimeBreakdown
        anchorEl={breakdown?.anchor || null}
        title={breakdown?.title}
        query={breakdown?.query}
        onClose={() => setBreakdown(null)}
      />
    </Dialog>
  )
}

/** A label above its value, for the row of facts at the top. */
function Fact({ label, value, capitalize = false }) {
  return (
    <Box sx={{ minWidth: 92 }}>
      <Typography variant="caption" color="text.secondary" display="block">
        {label}
      </Typography>
      <Typography variant="body2" fontWeight={600} sx={{ textTransform: capitalize ? "capitalize" : "none" }}>
        {value}
      </Typography>
    </Box>
  )
}
